'use client';

import React, { useState } from 'react';
import config from '../config/config';
import TermsAndConditions from './TermsAndConditions';
import TermsAndServicesType from '../models/terms';
import useSiteBusiness from '../hooks/useSiteBusiness';
import {
  useGetPublicProvincesQuery,
  useGetPublicCategoriesQuery,
} from '../store';
import Image from './atoms/Image';
import AvanttiIcon from './icons/AvanttiIcon';
import { ProvinceDTO, PublicCategoryDTO } from '../models/event';

// Categories shown inline; the rest live inside a `<details>` so the
// column stays tidy without dropping any links from the DOM. 3 + the
// "Ver todas" disclosure = 4 visible rows, matching the "Avantti"
// column to the right so the footer reads as a balanced grid.
const CATEGORIES_VISIBLE_TOP = 3;

interface FooterProps {
  /**
   * Pre-fetched categories from a server component (App Router pages /
   * layouts). When provided, the SSR HTML contains every category `<a>` —
   * which is what makes the SEO link equity actually flow. Omitted in
   * non-SSR contexts (e.g. the legacy SPA wrapper under `[...slug]`) where
   * the RTK Query fallback still hydrates the list client-side.
   */
  categories?: PublicCategoryDTO[];
  /** Same SSR-vs-fallback contract as `categories`. */
  provinces?: ProvinceDTO[];
}

const Footer: React.FC<FooterProps> = ({ categories: categoriesProp, provinces: provincesProp }) => {
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const { business, isLoading: isLoadingBusiness } = useSiteBusiness();
  const showSeoLinks = !business?.is_white_label;
  const { data: provincesRes } = useGetPublicProvincesQuery(undefined, { skip: !!provincesProp });
  const { data: categoriesRes } = useGetPublicCategoriesQuery(undefined, { skip: !!categoriesProp });
  // Hide hubs with no live events — empty hubs are bad UX and waste link
  // equity in the SEO footer.
  const provinces = (provincesProp ?? provincesRes?.data ?? []).filter((p) => p.event_count > 0);
  const categoriesAll = (categoriesProp ?? categoriesRes?.data ?? []).filter((c) => c.event_count > 0);
  // Most popular first so the inline slice surfaces the categories users
  // actually click; the long tail still ships in the DOM for crawlers.
  const categoriesSorted = [...categoriesAll].sort((a, b) => b.event_count - a.event_count);
  const categoriesTop = categoriesSorted.slice(0, CATEGORIES_VISIBLE_TOP);
  const categoriesRest = categoriesSorted.slice(CATEGORIES_VISIBLE_TOP);

  return (
    <>
      <div className="flex flex-col py-12 px-4 flex-col justify-between items-center gap-8 bg-neutral-900 text-white border-t border-border-tertiary">
        {showSeoLinks && (
          <nav
            aria-label="Enlaces SEO"
            className="flex flex-col gap-6 w-full max-w-6xl px-4 pb-6 border-b border-white/10"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
              <div className="flex flex-col gap-2">
                <h3 className="font-semibold text-white opacity-90 m-0">Por provincia</h3>
                <ul className="flex flex-col gap-1 list-none p-0 m-0">
                  {provinces.map((p) => (
                    <li key={p.slug} className="m-0">
                      <a href={`/eventos/${p.slug}`} className="opacity-70 hover:opacity-100 hover:underline">
                        Eventos en {p.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="font-semibold text-white opacity-90 m-0">Por categoría</h3>
                <ul className="flex flex-col gap-1 list-none p-0 m-0">
                  {categoriesTop.map((c) => (
                    <li key={c.slug} className="m-0">
                      <a href={`/categorias/${c.slug}`} className="opacity-70 hover:opacity-100 hover:underline">
                        {c.name}
                      </a>
                    </li>
                  ))}
                </ul>
                {categoriesRest.length > 0 && (
                  <details className="group m-0">
                    <summary className="cursor-pointer opacity-70 hover:opacity-100 hover:underline list-none [&::-webkit-details-marker]:hidden">
                      Ver todas las categorías
                      <span className="ml-1 inline-block transition-transform group-open:rotate-180" aria-hidden="true">▾</span>
                    </summary>
                    <ul className="flex flex-col gap-1 list-none p-0 mt-2 mb-0">
                      {categoriesRest.map((c) => (
                        <li key={c.slug} className="m-0">
                          <a href={`/categorias/${c.slug}`} className="opacity-70 hover:opacity-100 hover:underline">
                            {c.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="font-semibold text-white opacity-90 m-0">Avantti</h3>
                <ul className="flex flex-col gap-1 list-none p-0 m-0">
                  <li className="m-0">
                    <a href="/sobre-avantti" className="opacity-70 hover:opacity-100 hover:underline">
                      Sobre Avantti
                    </a>
                  </li>
                  <li className="m-0">
                    <a href="/preguntas-frecuentes" className="opacity-70 hover:opacity-100 hover:underline">
                      Preguntas frecuentes
                    </a>
                  </li>
                  <li className="m-0">
                    <a href="/guias" className="opacity-70 hover:opacity-100 hover:underline">
                      Guías y novedades
                    </a>
                  </li>
                  <li className="m-0">
                    <a href="https://avantti.com.ar/productores" target="_blank" rel="noopener noreferrer" className="opacity-70 hover:opacity-100 hover:underline">
                      Vendé con Avantti
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </nav>
        )}
        <div className='flex flex-col lg:flex-row items-center px-4 gap-6 self-stretch justify-between'>
          <div className="flex flex-row justify-center lg:justify-start w-full">
            {!business?.is_white_label && <AvanttiIcon className="inline-block mr-3 text-white" />}
            {business?.is_white_label && <Image
              isDownloading={isLoadingBusiness}
              src={business.logo}
              className="mr-3"
              alt="Business logo"
              height={40}
              width={120}
            />}
          </div>
          <div className="flex flex-col lg:flex-row items-center gap-3 w-full justify-center whitespace-nowrap">
            {((business && business?.is_white_label === false) || !business) && (
              <a
                href="https://avantti.com.ar/productores"
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer"
              >
                Quiero vender con avantti
              </a>
            )}
            <div>
              <a
                href="https://wa.me/message/GV2TPBX7VGT5A1"
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer"
              >
                Ayuda
              </a>
            </div>
            <div>
              <button
                type="button"
                className="cursor-pointer bg-transparent border-0 p-0 text-inherit font-inherit"
                onClick={() => setTermsModalOpen(true)}
              >
                Términos y condiciones
              </button>
            </div>
          </div>
          {((business && business?.is_white_label === false) || !business) && <div className="flex justify-center lg:justify-end items-center gap-6 w-full">
            <div>
              <a href="https://www.instagram.com/avantti.io/"><img src="/instagram-logo.svg" alt="Instagram" /></a>
            </div>
            <div>
              <a href="https://www.facebook.com/people/Avantti/61561762903235/"><img src="/facebook-icon.svg" alt="Facebook" /></a>
            </div>
            <div>
              <a href="https://www.linkedin.com/company/avantti-io"><img src="/linkedin-logo.svg" alt="Linkedin" /></a>
            </div>
            <div>
              <a href="https://avantti.com.ar/productores"><img src="/web-icon.svg" alt="Website" /></a>
            </div>
          </div>}
        </div>
        <div className="flex flex-col items-center text-xs text-white opacity-60 mt-3 font-light">
          <div>©Copyright @ 2026 avantti.com.ar</div>
          <div>All rights reserved.</div>
          <div className="text-xs absolute left-9">v{config.version}</div>
        </div>
      </div>

      <TermsAndConditions
        isOpen={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        type={TermsAndServicesType.BUYER}
      />
    </>
  );
};

export default Footer;
